import { useRef, useCallback, Dispatch, SetStateAction } from 'react';
import { preferMessageMedia, preferMessageStatus } from './chatSync.helpers';
import { ChatMessage } from './types';
/** Confirmação exige identidade; texto/horário iguais também podem ser envios legítimos. */
export function isConfirmedInDb(opt: ChatMessage, db: ChatMessage): boolean {
    return opt.id === db.id || opt.id === db.clientMessageId;
}

/** Eco sem media_url não pode apagar o blob do envio (flash “indisponível”). */
function mergeReplaceWithEcho(next: ChatMessage, echo?: ChatMessage): ChatMessage {
    if (!echo) return next;
    return {
        ...echo,
        ...next,
        media: preferMessageMedia(next.media, echo.media),
        status: preferMessageStatus(next.status, echo.status),
        clientMessageId: next.clientMessageId || echo.clientMessageId,
        quotedMessage: next.quotedMessage ?? echo.quotedMessage,
        reactions: next.reactions ?? echo.reactions,
    };
}

export function useChatOptimistic(
    activeConversationId: string | null,
    setCurrentMessages: Dispatch<SetStateAction<ChatMessage[]>>
) {
    const activeIdRef = useRef(activeConversationId);
    activeIdRef.current = activeConversationId;
    const optimisticMessagesMapRef = useRef<Record<string, ChatMessage[]>>({});

    const getPendingOptimistic = useCallback((convId: string): ChatMessage[] => {
        return optimisticMessagesMapRef.current[convId] || [];
    }, []);

    const addOptimisticMessage = useCallback((msg: ChatMessage) => {
        if (!activeConversationId) return;

        const withClientId: ChatMessage = {
            ...msg,
            clientMessageId: msg.clientMessageId || msg.id,
        };
        const current = optimisticMessagesMapRef.current[activeConversationId] || [];
        optimisticMessagesMapRef.current[activeConversationId] = [
            ...current.filter(m => m.id !== withClientId.id),
            withClientId,
        ];

        if (activeIdRef.current !== activeConversationId) return;
        setCurrentMessages(prev => prev.some(m => m.id === withClientId.id)
            ? prev.map(m => m.id === withClientId.id ? withClientId : m)
            : [...prev, withClientId]);
    }, [activeConversationId, setCurrentMessages]);

    const replaceOptimisticMessage = useCallback((oldId: string, newMsg: ChatMessage) => {
        if (!activeConversationId) return;

        // Mantém clientMessageId = temp-* para a key da lista não mudar (sem remount/piscada).
        const withClientId: ChatMessage = {
            ...newMsg,
            clientMessageId: newMsg.clientMessageId || oldId,
        };

        const writePending = (replacement: ChatMessage) => {
            const current = optimisticMessagesMapRef.current[activeConversationId] || [];
            optimisticMessagesMapRef.current[activeConversationId] = [
                ...current.filter(m => m.id !== oldId && m.id !== replacement.id),
                replacement,
            ];
        };

        if (activeIdRef.current !== activeConversationId) {
            writePending(withClientId);
            return;
        }
        setCurrentMessages(prev => {
            // Eco pode chegar antes do POST — mesclar sem perder blob/preview local.
            const confirmed = prev.find(m => m.id === withClientId.id && m.id !== oldId);
            const replacement = mergeReplaceWithEcho(withClientId, confirmed);
            writePending(replacement);
            const hasOld = prev.some(m => m.id === oldId);
            if (hasOld) return prev.flatMap(m => m.id === oldId
                ? [replacement] : m.id === replacement.id ? [] : [m]);
            return confirmed
                ? prev.map(m => m.id === replacement.id ? replacement : m)
                : [...prev, replacement];
        });
    }, [activeConversationId, setCurrentMessages]);

    const removeOptimisticMessage = useCallback((msgId: string) => {
        if (!activeConversationId) return;

        const current = optimisticMessagesMapRef.current[activeConversationId] || [];
        optimisticMessagesMapRef.current[activeConversationId] = current.filter(m => m.id !== msgId);

        if (activeIdRef.current === activeConversationId) {
            setCurrentMessages(prev => prev.filter(m => m.id !== msgId));
        }
    }, [activeConversationId, setCurrentMessages]);

    const getRemainingOptimisticMessages = useCallback((activeConvId: string, dbMessagesMap: Map<string, ChatMessage>) => {
        const currentOptimistic = optimisticMessagesMapRef.current[activeConvId] || [];
        const remainingOptimistic: ChatMessage[] = [];

        currentOptimistic.forEach(optMsg => {
            const dbMessage = [...dbMessagesMap.values()].find((row) => isConfirmedInDb(optMsg, row));

            if (dbMessage) {
                // Confirma no banco, mas preserva blob local até existir URL durável (evita “indisponível”).
                const media = preferMessageMedia(optMsg.media, dbMessage.media);
                const clientMessageId = dbMessage.clientMessageId || optMsg.clientMessageId || optMsg.id;
                if (media !== dbMessage.media || clientMessageId !== dbMessage.clientMessageId) {
                    dbMessagesMap.set(dbMessage.id, { ...dbMessage, media, clientMessageId });
                }
            } else {
                dbMessagesMap.set(optMsg.id, optMsg);
                remainingOptimistic.push(optMsg);
            }
        });

        optimisticMessagesMapRef.current[activeConvId] = remainingOptimistic;
        return dbMessagesMap;
    }, []);

    return {
        addOptimisticMessage,
        replaceOptimisticMessage,
        removeOptimisticMessage,
        getRemainingOptimisticMessages,
        getPendingOptimistic,
    };
}
